<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import agendaImage from 'dashboard/assets/real-estate/post-sale/agenda_posvenda.svg';

const props = defineProps({
  counts: { type: Object, required: true },
  userName: { type: String, default: '' },
});

const { t } = useI18n();
const items = computed(() => {
  const visibleItems = [
    {
      id: 'vermelhos',
      class: 'text-red-600',
      count: Number(props.counts.vermelhos || 0),
      label: count =>
        count === 1
          ? t('POST_SALE.AGENDA.SUMMARY.OVERDUE.ONE')
          : t('POST_SALE.AGENDA.SUMMARY.OVERDUE.OTHER'),
    },
    {
      id: 'laranjas',
      class: 'text-orange-600',
      count: Number(props.counts.laranjas || 0),
      label: count =>
        count === 1
          ? t('POST_SALE.AGENDA.SUMMARY.DUE_SOON.ONE')
          : t('POST_SALE.AGENDA.SUMMARY.DUE_SOON.OTHER'),
    },
    {
      id: 'amarelos',
      class: 'text-amber-600',
      count: Number(props.counts.amarelos || 0),
      label: count =>
        count === 1
          ? t('POST_SALE.AGENDA.SUMMARY.ATTENTION.ONE')
          : t('POST_SALE.AGENDA.SUMMARY.ATTENTION.OTHER'),
    },
  ].filter(item => item.count > 0);

  return visibleItems.map((item, index) => ({
    ...item,
    label: `${item.label(item.count)}${
      index === visibleItems.length - 1
        ? t('POST_SALE.AGENDA.SUMMARY.END')
        : t('POST_SALE.AGENDA.SUMMARY.SEPARATOR')
    }`,
  }));
});
const greeting = computed(() =>
  props.userName
    ? t('POST_SALE.AGENDA.SUMMARY.WELCOME_WITH_NAME', {
        name: props.userName,
      })
    : t('POST_SALE.AGENDA.SUMMARY.WELCOME')
);
</script>

<template>
  <LaisCard
    data-testid="post-sale-agenda-summary"
    density="flush"
    class="h-[147px] min-h-[140px] !rounded-[10px] !border-[#E0E7EB] !shadow-[0px_0px_6px_0px_rgba(0,0,0,0.02),0px_2px_4px_0px_rgba(0,0,0,0.08)] [&>div]:h-full"
  >
    <div
      data-testid="post-sale-agenda-summary-content"
      class="flex h-full min-h-0 flex-row items-center gap-[23px] overflow-hidden px-[21px] py-6"
    >
      <img
        :src="agendaImage"
        :alt="t('POST_SALE.AGENDA.SUMMARY.IMAGE_ALT')"
        class="h-auto w-[130px] shrink-0"
      />
      <div class="flex min-w-0 flex-col gap-2">
        <h2 class="font-[Roboto] text-xl font-bold leading-6 text-[#464F53]">
          {{ greeting }}
        </h2>
        <p class="mb-0 font-[Roboto] text-base leading-[normal] text-[#464F53]">
          <template v-if="items.length">
            {{ t('POST_SALE.AGENDA.SUMMARY.TODAY') }}
            <template v-for="item in items" :key="item.id">
              <span :class="item.class" class="font-bold">
                {{ item.count }} {{ item.label }}
              </span>
            </template>
          </template>
          <template v-else>
            {{ t('POST_SALE.AGENDA.SUMMARY.ON_TRACK') }}
          </template>
        </p>
      </div>
    </div>
  </LaisCard>
</template>
